import React, { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  Button,
  Collapse,
  DatePicker,
  Icons,
  Input,
  List,
  Select,
  Space,
  Table,
  TextField,
} from '@pankod/refine-antd'
import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
  LogicalFilter,
} from '@pankod/refine-core'
import Head from 'next/head'

import { TCertificateTTESubmission, TTTEStatus } from './types'
import { PengajuanStatusTag } from './components/PengajuanStatusTag'
import { SubmissionDetailDrawer } from './components/SubmissionDetailDrawer'
import { ParticipantsModal } from './components/ParticipantsModal'
import {
  formatCapabilityLabel,
  formatNumberValue,
  formatTextValue,
  renderCertificateTypeTag,
} from './display'
import useTableWithMeta from 'src/hooks/useTableWithMeta'
import Filter, { TFilterForm } from '@components/ContentFilter'

dayjs.extend(utc)
dayjs.extend(timezone)

const STATUS_OPTIONS: { label: string; value: TTTEStatus }[] = [
  { label: 'Menunggu Giliran', value: 'menunggu_persetujuan' },
  { label: 'Siap Diproses', value: 'siap_diproses' },
  { label: 'Dalam Proses', value: 'dalam_proses' },
  { label: 'Selesai', value: 'selesai' },
  { label: 'Ditolak', value: 'ditolak' },
  { label: 'TTD Ulang', value: 'tte_ulang' },
]

export const PenandatangananSertifikatList: React.FC<
  IResourceComponentsProps
> = () => {
  const {
    tableProps,
    tableQueryResult: { isFetching, refetch },
    searchFormProps,
    filters,
  } = useTableWithMeta<
    TCertificateTTESubmission,
    HttpError,
    TFilterForm<{
      tanggalPengajuan?: [Dayjs, Dayjs]
      nama_program?: string
      nama_penyelenggara?: string
      status?: TTTEStatus
    }>
  >({
    resource: 'certificate-tte-submissions',
    dataProviderName: 'lms',
    syncWithLocation: true,
    onSearch: (params) => {
      const filters: CrudFilters = []
      const { tanggalPengajuan, nama_program, nama_penyelenggara, status } =
        params

      filters.push(
        {
          field: 'tanggal_pengajuan_start',
          operator: 'eq',
          value: tanggalPengajuan?.[0].format('YYYY-MM-DD'),
        },
        {
          field: 'tanggal_pengajuan_end',
          operator: 'eq',
          value: tanggalPengajuan?.[1].format('YYYY-MM-DD'),
        },
      )

      filters.push({
        field: 'nama_program',
        operator: 'eq',
        value: nama_program,
      })

      filters.push({
        field: 'nama_penyelenggara',
        operator: 'eq',
        value: nama_penyelenggara,
      })

      filters.push({
        field: 'status',
        operator: 'eq',
        value: status,
      })

      const shouldAddTimestamp = [
        tanggalPengajuan,
        nama_program,
        nama_penyelenggara,
        status,
      ].some(Boolean)

      filters.push({
        field: 't',
        operator: 'eq',
        value: shouldAddTimestamp ? new Date().getTime() : undefined,
      })

      return filters
    },
  })

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [pesertaModalId, setPesertaModalId] = useState<string | null>(null)

  const hasFilters = filters?.some(({ value }) => Boolean(value))

  return (
    <List title="Penandatanganan Sertifikat">
      <Head>
        <title>LMS Admin | Penandatanganan Sertifikat</title>
      </Head>

      <Collapse className="!mb-4" bordered={false}>
        <Collapse.Panel
          header={
            <>
              {hasFilters ? (
                <Icons.FilterFilled
                  className="mr-1"
                  style={{ color: '#08c' }}
                />
              ) : (
                <Icons.FilterOutlined className="mr-1" />
              )}
              <TextField
                value="Filter Pengajuan"
                strong={Boolean(hasFilters)}
              />
            </>
          }
          key="filter-pengajuan"
        >
          <Filter
            layout="vertical"
            formProps={searchFormProps}
            filters={filters as LogicalFilter[]}
            size="small"
            items={[
              {
                label: 'Tanggal Pengajuan',
                name: 'tanggalPengajuan',
                component: (
                  <DatePicker.RangePicker
                    format="DD MMM YYYY"
                    allowEmpty={[false, false]}
                  />
                ),
              },
              {
                label: 'Nama Diklat',
                name: 'nama_program',
                component: (
                  <Input placeholder="Cari nama diklat" autoComplete="off" />
                ),
              },
              {
                label: 'Nama UPT',
                name: 'nama_penyelenggara',
                component: (
                  <Input placeholder="Cari nama UPT" autoComplete="off" />
                ),
              },
              {
                label: 'Status',
                name: 'status',
                component: (
                  <Select
                    options={STATUS_OPTIONS}
                    placeholder="Filter status"
                    allowClear
                  />
                ),
              },
            ]}
          />
        </Collapse.Panel>
      </Collapse>

      <Table<TCertificateTTESubmission>
        {...tableProps}
        loading={isFetching}
        rowKey="id"
        bordered
        size="small"
        scroll={{ x: 800 }}
        tableLayout="fixed"
      >
        <Table.Column<TCertificateTTESubmission>
          title="Tanggal Pengajuan"
          dataIndex="tanggalPengajuan"
          render={(value) => {
            if (!value) return '-'

            const parsedDate = dayjs(value)

            return parsedDate.isValid()
              ? parsedDate
                  .tz('Asia/Jakarta')
                  .format('YYYY-MM-DD HH:mm [(GMT+7)]')
              : '-'
          }}
          width={200}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Nama Diklat"
          dataIndex="namaDiklat"
          ellipsis
          render={(value) => formatTextValue(value)}
          width={200}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Nama UPT"
          dataIndex="namaPenyelenggara"
          ellipsis
          render={(value) => formatTextValue(value)}
          width={200}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Jumlah Kelas"
          dataIndex="jumlahKelas"
          align="center"
          render={(value: number) => formatNumberValue(value, 'Kelas')}
          width={120}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Jumlah Penerima"
          dataIndex="jumlahPeserta"
          align="center"
          width={150}
          render={(value: number) => formatNumberValue(value, 'Orang')}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Tipe Sertifikat"
          width={200}
          align="center"
          render={(_, record) => {
            const capabilityLabel = formatCapabilityLabel(
              record.kapabilitasSertifikat,
            )

            if (capabilityLabel === '-' || !record.tipeSertifikat) {
              return '-'
            }

            return (
              <div className="flex justify-between">
                <span>{capabilityLabel}</span>
                {renderCertificateTypeTag(record.tipeSertifikat)}
              </div>
            )
          }}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Status"
          dataIndex="status"
          align="center"
          render={(v) => <PengajuanStatusTag status={v} />}
          width={150}
        />
        <Table.Column<TCertificateTTESubmission>
          title="Aksi"
          align="center"
          fixed="right"
          width={180}
          render={(_, record) => (
            <Space direction="vertical" size={4}>
              <Button
                size="small"
                type="primary"
                icon={<Icons.EyeOutlined />}
                onClick={() => setSelectedId(record.id)}
                block
              >
                Lihat Detail
              </Button>
              <Button
                size="small"
                icon={<Icons.TeamOutlined />}
                onClick={() => setPesertaModalId(record.id)}
                block
              >
                Daftar Penerima
              </Button>
            </Space>
          )}
        />
      </Table>

      <SubmissionDetailDrawer
        open={!!selectedId}
        submissionId={selectedId}
        onClose={() => setSelectedId(null)}
        onSuccessRefetch={() => refetch()}
      />

      <ParticipantsModal
        open={!!pesertaModalId}
        submissionId={pesertaModalId}
        onClose={() => setPesertaModalId(null)}
      />
    </List>
  )
}
